import Facade from '@/Facade'
import GameSignals from '@/signals/GameSignals'
import { BaseChildState } from '@stateMachine/BaseChildState'
import gsap from 'gsap'
import {
    commitHoldAndWinWinToMeter,
    getHoldAndWinFinalWinAmount,
    isEndingHoldAndWinRound,
} from '@/states/holdandwin/HoldAndWinFinalWin'
import { isHoldAndWinInsideFreeSpins } from '@/states/freespins/FreeSpinsFinalWin'

export class HoldAndWinSummaryState extends BaseChildState {
    private _isCompleted: boolean = false

    public canTrigger(): boolean {
        // After this summary has run, do not re-enter on the FinalState rewalk to FS summary.
        if ((Facade as any).holdAndWinSummaryConsumed) {
            return false
        }

        return isEndingHoldAndWinRound(this._model)
    }

    public begin(): void {
        this._isCompleted = false
        // Credit Win Meter when the total-win popup opens (not during BigWin commendation).
        commitHoldAndWinWinToMeter(this._model)
        this.syncBalanceAfterHoldAndWin()
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onSummaryComplete)

        gsap.delayedCall(0.05, () => {
            if (this._isCompleted) {
                return
            }
            // H&W end popup (shared summary UI) with this feature's win — FS summary comes later.
            GameSignals.showFreeSpinsSummary.emit(this.getHoldAndWinTotalWinAmount())
        })
    }

    private getHoldAndWinTotalWinAmount(): number {
        return getHoldAndWinFinalWinAmount(this._model)
    }

    private isInsideFreeSpins(): boolean {
        return isHoldAndWinInsideFreeSpins(this._model)
    }

    private shouldRouteToFreeSpinsSummary(): boolean {
        const freebetAmountLeft = Number(this._model.spinData?.freebet?.amount_left ?? -1)
        return this.isInsideFreeSpins() && Number.isFinite(freebetAmountLeft) && freebetAmountLeft <= 0
    }

    private onSummaryComplete = (): void => {
        if (this._isCompleted) {
            return
        }

        this._isCompleted = true

        const deferredFreeSpinsAward = Number(this._model.spinData?.bonus?.freebet_given ?? 0)
        const freebetAmountLeft = Number(this._model.spinData?.freebet?.amount_left ?? -1)
        const shouldStartDeferredFreeSpins =
            !this._model.roundState.isFreeSpins &&
            Number.isFinite(deferredFreeSpinsAward) &&
            deferredFreeSpinsAward > 0 &&
            Number.isFinite(freebetAmountLeft) &&
            freebetAmountLeft > 0
        const shouldRouteToFreeSpinsSummary = this.shouldRouteToFreeSpinsSummary()

        this.clearHoldAndWinRuntimeFlags()
        ;(Facade as any).holdAndWinSummaryConsumed = true
        ;(GameSignals as any).holdAndWinStop?.emit()

        if (
            this.isInsideFreeSpins() &&
            !shouldStartDeferredFreeSpins &&
            !shouldRouteToFreeSpinsSummary
        ) {
            GameSignals.restoreFreeSpins.emit(this._model.spinData)
        }

        if (shouldStartDeferredFreeSpins) {
            ;(Facade as any).skipFinalWinPresentationAfterHoldAndWin = true
            this._parent.restartChild()
            return
        }

        // Last-FS H&W: after H&W commendation + end popup, rewalk FinalState for
        // FS total commendation + FreeSpinsSummary popup.
        if (shouldRouteToFreeSpinsSummary) {
            ;(Facade as any).skipFinalWinPresentationAfterHoldAndWin = true
            this._parent.restartChild()
            return
        }

        this.end()
    }

    private clearHoldAndWinRuntimeFlags(): void {
        this._model.spinState.isHoldAndWinActive = false
        ;(Facade as any).holdAndWinCollectInProgress = false
        ;(Facade as any).holdAndWinPendingBaseTransition = false

        if (this.isInsideFreeSpins()) {
            this._model.roundState.isFreeSpins = true
            this._model.isBonusGameNow = true
            return
        }

        this._model.isBonusGameNow = false
    }

    private syncBalanceAfterHoldAndWin(): void {
        const spinBalance = Number(this._model.spinData?.balance ?? 0)
        if (Number.isFinite(spinBalance) && spinBalance >= 0) {
            GameSignals.balanceHasChanged.emit(this._model.balance)
        }
    }

    public cleanUp(): void {
        GameSignals.showFreeSpinsSummaryComplete.disconnect(this.onSummaryComplete)
    }
}
